import React from 'react';
import { Button, DrawFeature, DrawSetting, DrawTool, IconButton } from '@agissc/ktag-map';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Switch } from '@ktag/styleguide/dist/react';
import useAppState from '../../../AppStore';
import useDrawMeasure from './useDrawMeasure';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import useDrawListElementFn from './useDrawListElementFn';

const DISPLAY_TOOLTIP_FOR_TOOLS = ['line', 'polygon', 'arrow', 'circle'];

interface Props {
  feature: DrawFeature;
}

const DrawListElement = ({ feature }: Props) => {
  const { t } = useTranslation('agisviewer');
  const { getMeasurements } = useDrawMeasure(feature);
  const { onSelectFeature } = useDrawListElementFn(feature);

  const setActiveView = useAppState((state) => state.setActiveView);
  const setActiveDrawTool = useAppState((state) => state.setActiveDrawTool);
  const removeDrawFeature = useAppState((state) => state.removeDrawFeature);
  const hoveredFeatureId = useAppState((state) => state.hoveredFeatureId);
  const setHoveredFeatureId = useAppState((state) => state.setHoveredFeatureId);
  const selectedFeatureId = useAppState((state) => state.selectedFeatureId);
  const setSelectedFeatureId = useAppState((state) => state.setSelectedFeatureId);
  const editDrawFeature = useAppState((state) => state.editDrawFeature);
  const setDrawSetting = useAppState((state) => state.setDrawSetting);

  const { Tracking } = useTracking();

  const onEdit = () => {
    Tracking.Draw.openElementSettings(feature.properties.type);
    setSelectedFeatureId(feature.id as string);
    setActiveDrawTool('selection');
    setHoveredFeatureId(null);
    setActiveView('draw_tool_settings');
  };

  const onDelete = () => {
    removeDrawFeature(feature.id as string);
  };

  const setChecked = (checked: boolean) => {
    Tracking.Draw.toggleMeasures(checked ? 'visible' : 'invisible');
    editDrawFeature(feature.id as string, {
      properties: { displayTooltip: checked },
    });
    setDrawSetting(DrawSetting.DisplayTooltip, checked);
  };

  const displayTooltipSwitch = DISPLAY_TOOLTIP_FOR_TOOLS.includes(
    feature?.properties.type as DrawTool,
  );

  return (
    <div
      className={classNames('draw-list__entry', {
        'draw-list__entry--selected': selectedFeatureId === feature.id,
      })}
    >
      <div>
        <Button additionalClassNames="draw-list__button" onClick={onSelectFeature}>
          <table>
            <tbody>
              {getMeasurements().map((measurement) => (
                <tr key={measurement.key}>
                  {measurement.key !== 'measure_text' && (
                    <td className="draw-list__key">
                      <span>{t(measurement.key as string)}</span>
                    </td>
                  )}
                  {'value' in measurement && (
                    <td
                      className={classNames(
                        'draw-list__value',
                        {
                          'draw-list__value--hovered': hoveredFeatureId === feature.id,
                        },
                        measurement.key === 'measure_text'
                          ? 'draw-list__value--text'
                          : '',
                      )}
                    >
                      {measurement.value}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Button>
        {displayTooltipSwitch && (
          <Switch
            label={t('draw_tool_display_tooltip')}
            checked={feature.properties.displayTooltip ?? false}
            setChecked={setChecked}
            additionalClassNames="draw-list__switch"
          />
        )}
      </div>
      <div className="draw-list__icon-wrapper">
        <IconButton icon="wastebin" title={t('draw_tooltip_delete')} onClick={onDelete} />
        <IconButton icon="gear" title={t('draw_tooltip_settings')} onClick={onEdit} />
      </div>
    </div>
  );
};

export default DrawListElement;
